"use client";

import { useLocale } from "../../../components/LocaleProvider";

type Props = {
  serviceName: string;
  /** English service name sent to the clinic inbox (defaults to serviceName). */
  serviceKey?: string;
};

const copy = {
  en: {
    title: "Request a Consultation",
    intro: (x: string) =>
      `Leave your details and our patient coordinator will contact you shortly to discuss your ${x.toLowerCase()} options.`,
    name: "Full Name *",
    email: "Email *",
    phone: "Phone *",
    message: "Message (optional)",
    submit: "Submit Request",
    ok: "Thank you — your request has been sent.",
    fail: "There was a problem sending your request. Please try again.",
    network: "Network error. Please try again.",
  },
  ro: {
    title: "Solicitați o consultație",
    intro: (x: string) =>
      `Lăsați-ne datele dumneavoastră, iar coordonatorul nostru pentru pacienți vă va contacta în scurt timp pentru a discuta opțiunile de tratament pentru: ${x.toLowerCase()}.`,
    name: "Nume complet *",
    email: "Email *",
    phone: "Telefon *",
    message: "Mesaj (opțional)",
    submit: "Trimite solicitarea",
    ok: "Mulțumim — solicitarea dumneavoastră a fost trimisă.",
    fail: "A apărut o problemă la trimiterea solicitării. Vă rugăm să încercați din nou.",
    network: "Eroare de rețea. Vă rugăm să încercați din nou.",
  },
} as const;

export default function ServiceRequestForm({ serviceName, serviceKey }: Props) {
  const locale = useLocale();
  const t = copy[locale];

  return (
    <div className="rounded-2xl border border-[#27E8A7]/40 bg-black/85 backdrop-blur-sm p-5 md:p-6 shadow-[0_0_20px_rgba(39,232,167,0.18)]">
      <h2 className="text-lg md:text-xl font-semibold text-white mb-3">
        {t.title}
      </h2>

      <p className="text-sm md:text-base text-white/80 mb-4">
        {t.intro(serviceName)}
      </p>

      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          const form = e.currentTarget as HTMLFormElement;

          const data = {
            name: (form.elements.namedItem("name") as HTMLInputElement).value,
            email: (form.elements.namedItem("email") as HTMLInputElement).value,
            phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
            service: serviceKey ?? serviceName,
            locale,
            message: (form.elements.namedItem("message") as HTMLTextAreaElement)
              ?.value,
          };

          try {
            const res = await fetch("/api/request-service", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(data),
            });

            if (res.ok) {
              alert(t.ok);
              form.reset();
            } else {
              alert(t.fail);
            }
          } catch {
            alert(t.network);
          }
        }}
      >
        <div>
          <label className="text-sm text-white/70">{t.name}</label>
          <input
            type="text"
            name="name"
            required
            className="w-full mt-1 p-2 rounded-md bg-black/40 border border-[#27E8A7]/30 text-white"
          />
        </div>

        <div>
          <label className="text-sm text-white/70">{t.email}</label>
          <input
            type="email"
            name="email"
            required
            className="w-full mt-1 p-2 rounded-md bg-black/40 border border-[#27E8A7]/30 text-white"
          />
        </div>

        <div>
          <label className="text-sm text-white/70">{t.phone}</label>
          <input
            type="tel"
            name="phone"
            required
            className="w-full mt-1 p-2 rounded-md bg-black/40 border border-[#27E8A7]/30 text-white"
          />
        </div>

        <div>
          <label className="text-sm text-white/70">{t.message}</label>
          <textarea
            name="message"
            rows={3}
            className="w-full mt-1 p-2 rounded-md bg-black/40 border border-[#27E8A7]/30 text-white"
          ></textarea>
        </div>

        <button type="submit" className="btn btn-primary w-full mt-4">
          {t.submit}
        </button>
      </form>
    </div>
  );
}
